import { createApp, type App } from 'vue'
import styles from './styles.css?inline'
import type { ContactMeOpenDetail, ContactMeWidgetOptions } from './types'
import ContactMeWidget from './components/ContactMeWidget.vue'
import { createWidgetI18n } from './i18n'

type ExposedApi = {
  open: (detail?: ContactMeOpenDetail) => void
  close: () => void
  configure: (options: ContactMeWidgetOptions) => void
}

function getAttr(el: HTMLElement, name: string): string | undefined {
  const v = el.getAttribute(name)
  return v && v.trim() ? v.trim() : undefined
}

export class BostContactMeWidgetElement extends HTMLElement {
  private shadow: ShadowRoot
  private app: App<Element> | undefined
  private api: ExposedApi | undefined
  private isOpen = false
  private opts: ContactMeWidgetOptions = {}
  private i18n = createWidgetI18n()

  private onWindowOpen = (e: Event) => {
    const detail = (e as CustomEvent<ContactMeOpenDetail>).detail
    this.open(detail)
  }

  private onWindowClose = () => {
    this.close()
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.isOpen) return
    if (e.key === 'Escape') {
      e.preventDefault()
      this.close()
    }
  }

  static get observedAttributes() {
    return ['submit-url', 'initial-submit-url', 'source', 'brand-name']
  }

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: 'open' })
  }

  connectedCallback() {
    this.mountVue()
    this.syncOptionsFromAttributes()

    window.addEventListener('opencontactme', this.onWindowOpen)
    window.addEventListener('closecontactme', this.onWindowClose)
    window.addEventListener('keydown', this.onKeyDown)
  }

  disconnectedCallback() {
    window.removeEventListener('opencontactme', this.onWindowOpen)
    window.removeEventListener('closecontactme', this.onWindowClose)
    window.removeEventListener('keydown', this.onKeyDown)
    this.unmountVue()
  }

  attributeChangedCallback() {
    this.syncOptionsFromAttributes()
  }

  configure(options: ContactMeWidgetOptions) {
    this.opts = { ...this.opts, ...options }
    this.api?.configure(this.opts)
  }

  open(detail?: ContactMeOpenDetail) {
    this.isOpen = true
    this.api?.open(detail)
  }

  close() {
    this.isOpen = false
    this.api?.close()
  }

  private syncOptionsFromAttributes() {
    const submitUrl = getAttr(this, 'submit-url')
    const initialSubmitUrl = getAttr(this, 'initial-submit-url')
    const source = getAttr(this, 'source')
    const brandName = getAttr(this, 'brand-name')
    this.opts = {
      ...this.opts,
      ...(submitUrl ? { submitUrl } : {}),
      ...(initialSubmitUrl ? { initialSubmitUrl } : {}),
      ...(source ? { source } : {}),
      ...(brandName ? { brandName } : {}),
    }
    this.api?.configure(this.opts)
  }

  private mountVue() {
    if (this.app) return

    const style = document.createElement('style')
    style.textContent = styles
    this.shadow.appendChild(style)

    const mountEl = document.createElement('div')
    this.shadow.appendChild(mountEl)

    const app = createApp(ContactMeWidget)
    app.use(this.i18n)
    const instance = app.mount(mountEl) as unknown as ExposedApi
    this.app = app
    this.api = instance

    if (Object.keys(this.opts).length) {
      this.api.configure(this.opts)
    }
  }

  private unmountVue() {
    try {
      this.app?.unmount()
    } finally {
      this.app = undefined
      this.api = undefined
      // Keep shadow contents; element is going away anyway.
    }
  }
}
